import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const problemsData = [
  { id: 1, problem: 'Drowning in repetitive manual tasks?', solution: 'We deploy AI automation that handles routine work, freeing your team for strategic initiatives.' },
  { id: 2, problem: 'Struggling with customer support volume?', solution: 'We build AI chatbots that provide instant 24/7 support, reducing response time by 90%.' },
  { id: 3, problem: 'Missing insights hidden in your data?', solution: 'We create ML models that uncover patterns and predict trends for smarter decisions.' },
  { id: 4, problem: 'Want to scale sales without hiring?', solution: 'We develop AI calling systems that qualify leads and schedule appointments automatically.' },
  { id: 5, problem: 'Need to process visual data at scale?', solution: 'We implement computer vision systems that analyze images and videos with superhuman accuracy.' },
  { id: 6, problem: 'Overwhelmed by text data analysis?', solution: 'We deploy NLP solutions that extract insights from documents and customer feedback instantly.' },
  { id: 7, problem: 'Competitors leveraging AI while you lag?', solution: 'We fast-track your AI adoption with proven solutions tailored to your industry.' },
  { id: 8, problem: 'High operational costs eating profits?', solution: 'We optimize processes with AI, typically reducing costs by 40-60% within months.' }
];

const ProblemSolution = () => {
  const [flipped, setFlipped] = useState({});

  const handleFlip = (id) => {
    setFlipped((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Struggling With Technology That Holds Your Business Back?
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            We've helped 500+ businesses overcome these exact challenges with smart, AI-powered solutions.
          </p>
          <p className="text-orange-400 font-medium mt-4">
            Hover over cards to see how we solve each problem →
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {problemsData.map((item) => (
            <div
              key={item.id}
              className="relative h-64 cursor-pointer group"
              onMouseEnter={() => handleFlip(item.id)}
              onMouseLeave={() => handleFlip(item.id)}
            >
              <div className="relative w-full h-full transition-transform duration-500 transform-gpu">
                {/* Front */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 flex flex-col justify-between transition-opacity duration-300 ${
                    flipped[item.id] ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  <div>
                    <div className="text-orange-400 font-semibold text-sm mb-3">Problem</div>
                    <h3 className="text-lg font-bold text-white leading-tight">{item.problem}</h3>
                  </div>
                  <div className="text-gray-400 text-sm flex items-center gap-2">
                    Hover to see solution
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>

                {/* Back */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-orange-600 to-amber-600 rounded-xl p-6 flex flex-col justify-between transition-opacity duration-300 ${
                    flipped[item.id] ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div>
                    <div className="text-orange-100 font-semibold text-sm mb-3">Solution</div>
                    <p className="text-white leading-relaxed">{item.solution}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-white text-xl mb-6">Ready to transform these challenges into opportunities?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Free Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500/10"
              onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Schedule a Call
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;